/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/react';

import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { css, List, ListItemButton, Box } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { useNavigate } from 'react-router';

type MenuProps = {
  changeId: string;
  pathname: string;
  isResourcesVisible: boolean | '' | undefined;
  isCalcVisible: boolean | '' | undefined;
  isExternVisible: boolean | '' | undefined;
  isImplementationVisible: boolean | '' | undefined;
  highlightString: string;
};

const getMenuItemStyle = (pathname: string, route: string) => css`
  background-color: ${pathname.includes(route) ? '#e3f2fd' : 'none'};
  border-left: ${pathname.includes(route) ? '4px solid #00223B' : 'none'};
  font-weight: ${pathname.includes(route) ? 'bold' : 'normal'};
`;

const MainMenuList = observer(
  ({
    changeId,
    pathname,
    isResourcesVisible,
    isCalcVisible,
    isExternVisible,
    isImplementationVisible,
    highlightString,
  }: MenuProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const MenuItemContent = observer(
      ({
        text,
        route,
        disabled,
        onClick,
      }: {
        text: string;
        route: string;
        disabled?: boolean;
        onClick: () => void;
      }) => (
        <ListItemButton
          css={getMenuItemStyle(pathname, route)}
          onClick={onClick}
          disabled={disabled}
        >
          <Box
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              width: 100%;
            `}
          >
            <span>{text}</span>
            {highlightString === route && (
              <WarningIcon
                css={css`
                  color: orange;
                  font-size: 20px;
                `}
              />
            )}
          </Box>
        </ListItemButton>
      ),
    );

    return (
      <List
        css={css`
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding-top: 32px;
          font-size: 12px;
          & .MuiListItemButton-root {
            padding-left: 24px;
          }
        `}
      >
        <MenuItemContent
          text="Change Details"
          route="edit"
          onClick={() => navigate(`/change-request/${changeId}/edit`)}
        />
        <MenuItemContent
          text={t('navigation.resources')}
          route="resources"
          disabled={!isResourcesVisible}
          onClick={() =>
            !pathname.includes('resources') &&
            navigate(`/change-request/${changeId}/resources`)
          }
        />
        <MenuItemContent
          text={t('navigation.calculation')}
          route="calculation"
          disabled={!isCalcVisible}
          onClick={() => navigate(`/change-request/${changeId}/calculation`)}
        />
        <MenuItemContent
          text={t('navigation.approvalStep')}
          route="approval"
          disabled={!isExternVisible}
          onClick={() => navigate(`/change-request/${changeId}/approval`)}
        />
        <MenuItemContent
          text={t('navigation.implementation')}
          route="implementation"
          disabled={!isImplementationVisible}
          onClick={() => navigate(`/change-request/${changeId}/implementation`)}
        />
        <MenuItemContent
          text={t('navigation.attachments')}
          route="attachments"
          disabled={!changeId}
          onClick={() => navigate(`/change-request/${changeId}/attachments`)}
        />
      </List>
    );
  },
);

export default MainMenuList;
